import {ApiError, apiFetch} from "../client";
import {apiErrorCode} from "../error-codes";
import {extractApiErrorBody, formatApiError} from "./errors";

// Автопополнение TRC20: привязка watch-only адреса, история подтверждённых
// зачислений и ручное восстановление окна. Зачисляет деньги воркер на бэкенде —
// здесь только чтение и управление адресом.

export interface MerchantDepositAddress {
    id: string;
    network: "trc20";
    address: string;
    activeFrom: string;
    enabled: boolean;
    disabledAt: string | null;
}

export interface MerchantDepositAddressListResponse {
    items: MerchantDepositAddress[];
}

export interface MerchantChainDeposit {
    network: "trc20";
    tokenContract: string;
    txId: string;
    eventIndex: number;
    /** USDT decimal string from the backend; never convert it to a JS number. */
    amount: string;
    from: string;
    blockTimestamp: string;
    creditedAt: string;
}

const SHASTA_USDT_CONTRACT = "TG3XXyExBkPp9nzdajDZsozEu4BkaSJozs";

/** Build the explorer URL from the persisted deposit contract, not build-time config. */
export function merchantChainDepositExplorerUrl(
    deposit: Pick<MerchantChainDeposit, "txId" | "tokenContract">,
): string {
    const explorer = deposit.tokenContract === SHASTA_USDT_CONTRACT
        ? "https://shasta.tronscan.org/#/transaction/"
        : "https://tronscan.org/#/transaction/";
    return `${explorer}${encodeURIComponent(deposit.txId)}`;
}

export interface MerchantChainDepositListResponse {
    items: MerchantChainDeposit[];
}

export interface MerchantChainBackfillResponse {
    found: number;
    credited: number;
    duplicates: number;
    skipped: number;
}

export function getMerchantDepositAddresses(
    token: string | null,
    id: string,
): Promise<MerchantDepositAddressListResponse> {
    return apiFetch<MerchantDepositAddressListResponse>(
        `/merchant/${encodeURIComponent(id)}/deposit-addresses`,
        token,
    );
}

export function setMerchantDepositAddress(
    token: string | null,
    id: string,
    address: string,
): Promise<MerchantDepositAddress> {
    return apiFetch<MerchantDepositAddress>(
        `/merchant/${encodeURIComponent(id)}/deposit-addresses`,
        token,
        {
            method: "PUT",
            body: JSON.stringify({network: "trc20", address}),
        },
    );
}

export function deleteMerchantDepositAddress(
    token: string | null,
    id: string,
): Promise<void> {
    return apiFetch<void>(
        `/merchant/${encodeURIComponent(id)}/deposit-addresses/trc20`,
        token,
        {method: "DELETE"},
    );
}

export function disableMerchantDepositAddress(
    token: string | null,
    id: string,
    addressId: string,
): Promise<void> {
    return apiFetch<void>(
        `/merchant/${encodeURIComponent(id)}/deposit-addresses/${encodeURIComponent(addressId)}/disable`,
        token,
        {method: "POST"},
    );
}

export function enableMerchantDepositAddress(
    token: string | null,
    id: string,
    addressId: string,
    replaceCurrent: boolean,
): Promise<MerchantDepositAddress> {
    return apiFetch<MerchantDepositAddress>(
        `/merchant/${encodeURIComponent(id)}/deposit-addresses/${encodeURIComponent(addressId)}/enable`,
        token,
        {
            method: "POST",
            body: JSON.stringify({replaceCurrent}),
        },
    );
}

export function getMerchantChainDeposits(
    token: string | null,
    id: string,
    limit = 50,
): Promise<MerchantChainDepositListResponse> {
    const params = new URLSearchParams({limit: String(limit)});
    return apiFetch<MerchantChainDepositListResponse>(
        `/merchant/${encodeURIComponent(id)}/chain-deposits?${params.toString()}`,
        token,
    );
}

export function backfillMerchantChainDeposits(
    token: string | null,
    id: string,
    hours: number,
): Promise<MerchantChainBackfillResponse> {
    return apiFetch<MerchantChainBackfillResponse>(
        `/merchant/${encodeURIComponent(id)}/chain-backfill`,
        token,
        {
            method: "POST",
            body: JSON.stringify({network: "tron-mainnet", hours}),
        },
    );
}

export function formatDepositAddressError(err: unknown): string {
    if (err instanceof ApiError) {
        switch (apiErrorCode(err)) {
            case "invalid_address":
                return "Некорректный TRON-адрес: проверьте формат и контрольную сумму.";
            case "address_already_bound":
                return "Этот адрес уже привязан к другому мерчанту.";
            case "deposit_address_exists":
                return "У мерчанта уже есть активный TRC20-адрес.";
            case "deposit_address_disabled":
                return "Этот адрес уже сохранён в отключённых. Включите его из списка адресов.";
            case "address_has_deposits":
                return "Адрес уже получал зачисления и не может быть физически удалён.";
            case "merchant_archived":
                return "Архивному мерчанту нельзя менять адрес автопополнения.";
            default:
                break;
        }
    }
    return formatApiError(err);
}

export function formatChainBackfillError(err: unknown): string {
    if (err instanceof ApiError) {
        const detail = extractApiErrorBody(err)?.error;
        if (err.status === 404) {
            return "Сначала привяжите активный TRC20-адрес.";
        }
        if (err.status === 503 && detail === "chain recovery is disabled") {
            return "Восстановление временно выключено оператором (CHAIN_ENABLED=false).";
        }
        if (err.status === 503) {
            return "TronGrid или восстановление временно недоступны. Повторите запрос позже без изменения окна.";
        }
    }
    return formatApiError(err);
}
